
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import mapValues from "lodash/mapValues";
import omitBy from "lodash/omitBy";
import uniq from "lodash/uniq";
import Vue from "vue";
import {
    getActivationList,
    getAudience,
    getBotChannelList,
    getClientYcCategoryList,
    getProductWithoutAbonementList,
    getServiceByCategoryList,
    getServiceCategoryList,
    getStaffList,
    getTagSubgroupList,
} from "../../../../../api/chisai/GCB2";
import { checkRole } from "../../../../../helpers/roles";
import { Audience } from "../../../../../types/GCB2";
import {
    AUDEINCE_ADDITIONAL_SETTINGS,
    CHANGE_ACTIVATION_TYPES,
    MESSAGING_CHANNELS_FLAT_LIST_KEY,
} from "../../../../../vars/GCB2";
import { CONNECTOR_TYPES, TAG_COLORS } from "../../../../../vars/general";
import BaseInputOutlined from "../../../../inputs/BaseInputOutlined.vue";
import Tags from "../../../../inputs/Tags/Tags.vue";
import TextField from "../../../../inputs/TextField.vue";
import SpinnerLoader from "../../../../SpinnerLoader.vue";
import FileUploader from "../../../../ui/FileUploader.vue";
import { Project, Tag } from "@/types/main";
import { isBlank, mod } from "@/utils";
import cfg from "@root/config";
import AudienceListItem from "./AudienceListItem.vue";
import AudienceTitleWithParams from "./AudienceTitleWithParams.vue";
import { normalizeBotChannelList } from "../../utils/botChannelList";

const FETCH_LIST_FUNCTIONS: any = {
    staff_list: getStaffList,
    service_list: getServiceByCategoryList,
    category_list: getServiceCategoryList,
    activation_list: getActivationList,
    tag_list: getTagSubgroupList,
    bot_channel_list: getBotChannelList,
    product_list: getProductWithoutAbonementList,
}
const xlsMimeTypes = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformatsofficedocument.spreadsheetml.template",
  "application/msexcel",
  "application/x-msexcel",
  "application/x-ms-excel",
  "application/x-excel",
  "application/x-dos_ms_excel",
  "application/xls",
  "application/x-xls",
];


export default Vue.extend({
    components: {
        AudienceTitleWithParams,
        TextField,
        SpinnerLoader,
        AudienceListItem,
        BaseInputOutlined,
        Tags,
        FileUploader,
    },
    props: {
        value: { type: Object },
        mode: { type: String },
    },
    data: () => ({
        audienceFilterValues: {} as any,
        loading: true,
        audienceList: [] as Audience[],
        audienceRules: [(v: any) => !!v || "Обязательно"],
        lists: {} as any,
        selectedTags: [] as string[],
        search: "",
        fromFile: false,
        xlsTypes: xlsMimeTypes,
    }),
    computed: {
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id);
        },
        isHrProject(): boolean {
            return this.project.connectors.find((con) => con.connectorType === CONNECTOR_TYPES.GCB2)?.hrProject || false;
        },
        pseudoNetworkProjects(): Project[] {
            return this.$store.getters.pseudoNetworkProjects || [];
        },
        filteredAudienceList(): any[] {
            const titleFilter = (audience: Audience) =>
                this.search
                    ? this.search
                          .trim()
                          .toLowerCase()
                          .split(" ")
                          .every((word) => audience?.name.toLowerCase().includes(word)) ||
                      String(audience?.id)
                          .toLowerCase()
                          .includes(this.search.trim().toLowerCase())
                    : true;
            const tagFilter = (audience: Audience) =>
                this.selectedTags.every((tag) => audience?.tags.includes(tag));
            const isVisible = (audience: Audience) => !audience?.extra?.hidden;
            return this.audienceList.filter((el) => isVisible(el) && titleFilter(el) && tagFilter(el));
        },
        tagsList(): Tag[] {
            return uniq(this.audienceList.filter((el) => !el?.extra?.hidden).flatMap((el) => el.tags)).map((tagName, i) => ({
                id: tagName,
                name: tagName,
                color: TAG_COLORS[mod(i, TAG_COLORS.length)],
            }));
        },
        selectedAudience(): Audience {
            return this.audienceList.find((el) => el.id === this.value.audienceId) as Audience;
        },
        yClientsConnector() {
            return this.$store.getters
                .projectById(this.$router.currentRoute.params.id)
                .connectors.find((el) => el.connectorType === CONNECTOR_TYPES.yClients);
        },
        type() {
            return this.$route.query.type;
        },
    },
    watch: {
        "value.audienceId"() {
            this.setFilter(this.value.audienceId);
        },
        "audienceFilterValues": {
            deep: true,
            handler() {
                this.setFilter(this.value.audienceId);
            },
        },
    },
    methods: {
        checkRole,
        formInput(e: any) {
            const isValid = () => {
                if (this.fromFile && this.mode === CHANGE_ACTIVATION_TYPES.create) {
                    return Boolean(this.value.excelFile?.file);
                } else {
                    return !isNil(this.value.audienceId);
                }
            };
            this.$emit("validation-updated", isValid());
        },
        clear() {
            if (this.type !== "upload") {
                this.fromFile = false;
            }
            this.search = ""
            ; (this.$refs as any).form.resetValidation()
            ; (this.$refs as any).excelUploader?.clearFile();
        },
        selectAudience(e: Audience | null) {
            this.value.audienceId = e?.id;
            const exp1 = cfg.excelActivationId === e?.id;
            const exp2 = cfg.uploadActivationId === e?.id;
            this.fromFile = exp1 || exp2;
        },
        // TODO: Можно упросить в связи с новым дизайном
        initAudienceFilterValues() {
            const audienceFilterValues = {} as any;
            this.audienceList.forEach((audience: Audience) => {
                const filterValue = {} as any;
                const schema = audience?.settings.schema;
                const additionalParamsList = audience?.settings.additional_params_list;

                const applyDefaultsFromItem = (schemaItem: any) => {
                    if (!schemaItem) {
                        return;
                    }
                    if (Array.isArray(schemaItem)) {
                        schemaItem.forEach(applyDefaultsFromItem);
                        return;
                    }
                    if (schemaItem.valueField) {
                        let def = schemaItem.defaultValue;
                        if (def === undefined && schemaItem.multiple) { def = []; }
                        const value =
                            this.value.audienceId === audience?.id
                                ? this.value.extra?.filter?.[schemaItem.valueField] ?? def
                                : def;
                        filterValue[schemaItem.valueField] = value;
                    }

                    if (Array.isArray(schemaItem.block_schema)) {
                        schemaItem.block_schema.forEach(applyDefaultsFromItem);
                    }
                    if (Array.isArray(schemaItem.content)) {
                        schemaItem.content.forEach(applyDefaultsFromItem);
                    }
                };

                if (schema) {
                    schema.flat().forEach((el: any) => {
                        applyDefaultsFromItem(el);
                    });
                }
                if (additionalParamsList) {
                    additionalParamsList.forEach((param: string) => {
                        const paramSchema = get(AUDEINCE_ADDITIONAL_SETTINGS, `${param}.schema`);
                        if (Array.isArray(paramSchema)) {
                            const paramSchemaRows = paramSchema as any[];
                            paramSchemaRows.flat().forEach((el: any) => applyDefaultsFromItem(el));
                        }
                        const defautValue = get(AUDEINCE_ADDITIONAL_SETTINGS, `${param}.defaultValue`);
                        if (defautValue !== undefined) {
                            filterValue[param] =
                                this.value.audienceId === audience?.id
                                    ? this.value.extra?.filter[param] ?? defautValue
                                    : defautValue;
                        }
                    });
                }
                if (this.value.audienceId === audience?.id) {
                    Object.assign(filterValue, this.value.extra?.filter);
                }
                audienceFilterValues[audience?.id] = filterValue;
            });
            this.audienceFilterValues = audienceFilterValues;
        },
        setFilter(audienceId: number) {
            const val = mapValues(this.audienceFilterValues[audienceId], (o) => (isBlank(o) ? null : o));
            const audience = this.audienceList.find((el) => el?.id === audienceId);
            const filterInitValue = audience?.settings?.parameterized ? {} : this.value?.extra?.filter || null;
            const filter = isEmpty(val) ? filterInitValue : omitBy(val, isNil);
            this.value.extra = Object.assign({}, this.value.extra, { filter });
        },
        buildMessagingChannelsFlatList() {
            const connectors = this.project?.connectors || [];
            const rows: Array<{ id: string; name: string }> = [];
            connectors.forEach((con: any, idx: number) => {
                const channelType = con?.channelType || "";
                const providerType = con?.providerType || "";
                let name: string | null = null;
                if (channelType === "whatsappChannel" || ["whatsappBasis", "whatsappApi", "whatsapp", "dh-whatsapp"].includes(providerType)) {
                    name = "WhatsApp";
                } else if (channelType === "telegramChannel" || ["telegramApi", "telegramDh"].includes(providerType)) {
                    name = "Telegram";
                } else if (channelType === "maxBusiness" || ["maxBusiness", "maxBasis"].includes(providerType)) {
                    name = "MAX Business";
                }
                if (!name) { return; }
                rows.push({
                    id: String(con?.connectorId || con?.id || `${name}-${idx}`),
                    name,
                });
            });
            return rows;
        },
        collectSchemaItems(items: any[], acc: any[] = []): any[] {
            (items || []).forEach((item) => {
                if (!item) {
                    return;
                }
                if (Array.isArray(item)) {
                    this.collectSchemaItems(item, acc);
                    return;
                }
                acc.push(item);
                if (Array.isArray(item.block_schema)) {
                    this.collectSchemaItems(item.block_schema, acc);
                }
                if (Array.isArray(item.content)) {
                    this.collectSchemaItems(item.content, acc);
                }
            });
            return acc;
        },
        async fetchLists() {
            const fromMain = this.audienceList.flatMap((el: any) =>
                this.collectSchemaItems(el.settings.schema?.flat() || []),
            );
            const fromAdditional = this.audienceList.flatMap((el: any) =>
                (el.settings.additional_params_list || []).flatMap((paramKey: string) => {
                    const sch = get(AUDEINCE_ADDITIONAL_SETTINGS, `${paramKey}.schema`) || [];
                    return sch.filter((x: any) => x?.type === "select" && x.list).map((x: any) => x.list);
                }),
            );
            const needChannels = Array.isArray(this.value?.tags) && this.value.tags.includes("subscribers_growth");
            const fromMainLists = fromMain.filter((x: any) => x?.type === "select" && x.list).map((x: any) => x.list);
            const listsToFetch = uniq(
                [...fromMainLists, ...fromAdditional, ...(needChannels ? [MESSAGING_CHANNELS_FLAT_LIST_KEY] : [])].filter(Boolean),
            );
            await Promise.all(
                listsToFetch.map(async (el: any) => {
                    if (el === MESSAGING_CHANNELS_FLAT_LIST_KEY) {
                        this.lists[el] = Object.freeze(this.buildMessagingChannelsFlatList());
                        return;
                    }
                    const fetch = FETCH_LIST_FUNCTIONS[el];
                    if (fetch) {
                        const list = await fetch({ projectId: this.project.id }).then((el: any) => el.data)
                        this.lists[el] = Object.freeze(
                            el === 'bot_channel_list'
                                ? normalizeBotChannelList(list, this.project.connectors)
                                : list
                        )
                    }
                }),
            );
            if (this.yClientsConnector) {
                this.lists.client_category_list = await getClientYcCategoryList({ projectId: this.project.id }).then((el: any) => el.data);
            }
            if (this.isHrProject) {
                this.lists.filial_list = this.pseudoNetworkProjects?.filter((el) => !el.isMain);
                this.lists.specialization_list = ["X9FUHP3L5", "XSTM8F6WE"].includes(this.project.id) ? [
                    {name: "Ногтевой мастер", id: "Ногтевой мастер"},
                    {name: "Бровист/лашмейкер", id: "Бровист/лашмейкер"},
                    {name: "Массажист", id: "Массажист"},
                    {name: "Визажист-стилист/Парикмахер", id: "Визажист-стилист/Парикмахер"},
                    {name: "Мастер эпиляции", id: "Мастер эпиляции"},
                    {name: "Администратор", id: "Администратор"},
                    {name: "Управляющий", id: "Управляющий"},
                    {name: "Собственник", id: "Собственник"},
                ] : [
                    {name: "Администратор", id: "Администратор"},
                    {name: "Косметолог", id: "Косметолог"},
                    {name: "Мастер маникюра", id: "Мастер маникюра"},
                    {name: "Парикмахер", id: "Парикмахер"},
                    {name: "Другое", id: "Другое"},
                ];
            }
        },
        showAudienceAdditionalParams(audience: any) {
            return audience?.settings.additional_params_list && audience?.id === this.value.audienceId;
        },
    },
    async created() {
        this.loading = true;
        const rawAudience = await getAudience(this.project.id, this.isHrProject);
        let baseList = [];
        if (rawAudience && rawAudience.error) {
            this.$store.dispatch("callNotify", String(rawAudience.error.message || "Не удалось загрузить аудитории"));
        } else {
            baseList = Array.isArray(rawAudience) ? rawAudience : [];
        }
        this.audienceList = Object.freeze(baseList) as unknown as Audience[];
        await this.fetchLists();
        this.initAudienceFilterValues();
        if (this.type === "upload" && this.mode === CHANGE_ACTIVATION_TYPES.create && this.yClientsConnector) {
            const uploadAudience = this.audienceList.find((el) => el.id === cfg.uploadActivationId);
            if (uploadAudience) {
                this.selectAudience(uploadAudience);
            } else {
                this.$store.dispatch("callNotify", "Ошибка: Аудитория не найдена");
            }
        }
        this.loading = false;
        if (!this.value.excelFile) {
            this.value.excelFile = {};
        }
        this.fromFile = [cfg.excelActivationId, cfg.uploadActivationId].includes(this.value.audienceId);
    },
    mounted() {
        this.$emit("validation-updated", !isNil(this.value.audienceId));
    },
});
